import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useStore } from "@/context/StoreContext";
import { CATEGORIES } from "@/data/products";
import { Reveal } from "@/components/Reveal";

const SPANS = ["md:col-span-2 md:row-span-2", "md:col-span-2", "", "", "md:col-span-2", "", ""];

const titleCase = (s) => s.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

export default function CategoryGrid({ data }) {
  const eyebrow = data?.eyebrow || "Sacred Rituals";
  const { catalog } = useStore();
  const navigate = useNavigate();

  const subs = catalog.categories.filter((c) => c.parent_id);
  const source = subs.length ? subs : catalog.categories;
  const live = source.slice(0, 7).map((c, i) => ({
    id: c.id,
    name: titleCase(c.name),
    count: c.product_count || 0,
    img: CATEGORIES[i % CATEGORIES.length].img,
    span: SPANS[i % SPANS.length],
  }));

  const tiles = live.length
    ? live
    : CATEGORIES.map((c, i) => ({ id: null, name: c.name, count: c.count, img: c.img, span: SPANS[i] }));

  return (
    <section id="categories" data-testid="categories-section" className="mx-auto max-w-7xl px-4 py-10 sm:px-6 sm:py-14">
      <Reveal>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-gold">{eyebrow}</p>
            <h2 className="mt-3 font-display text-3xl leading-tight tracking-tight text-cream sm:text-5xl">
              {data?.heading || (<>Choose your <span className="italic text-gold">ritual</span></>)}
            </h2>
          </div>
          <p className="max-w-xs text-sm leading-relaxed text-fern">
            Every category, every house, one shelf. Tap a ritual to explore its edit.
          </p>
        </div>
      </Reveal>

      <div className="mt-7 grid auto-rows-[190px] grid-cols-2 gap-3 sm:auto-rows-[220px] sm:gap-4 md:grid-cols-4">
        {tiles.map((c, i) => (
          <motion.button
            key={c.id || c.name}
            data-testid={`category-tile-${(c.id || c.name).toString().replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, delay: i * 0.07, ease: [0.22, 1, 0.36, 1] }}
            onClick={() => navigate(c.id ? `/category/${c.id}` : "/shop")}
            className={`group relative overflow-hidden rounded-2xl border border-champagne/40 text-left ${c.span}`}
          >
            <img src={c.img} alt={c.name} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-110" />
            <div className="absolute inset-0 bg-gradient-to-t from-forest/90 via-forest/25 to-transparent transition-opacity duration-500 group-hover:from-forest/95" />
            <span className="absolute left-4 top-4 font-mono text-[10px] tracking-[0.3em] text-gold">0{i + 1}</span>
            <span className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full border border-cream/20 bg-white/60 text-cream opacity-0 backdrop-blur transition-all duration-500 group-hover:opacity-100">
              <ArrowUpRight className="h-4 w-4" />
            </span>
            <div className="absolute bottom-4 left-4 right-4">
              <h3 className="font-display text-xl text-cream sm:text-2xl">{c.name}</h3>
              <p className="mt-1 font-mono text-[9px] uppercase tracking-[0.25em] text-sage">{c.count} products</p>
            </div>
          </motion.button>
        ))}
      </div>
    </section>
  );
}
